// Non-component helper (see apps/web/CLAUDE.md — .tsx files export components
// only).
import { isValidPhoneNumber, type CountryCode } from 'libphonenumber-js';

export type PhoneValidationResult = {
  valid: boolean;
  error?: string;
};

const EMPTY_ERROR = 'Enter your phone number.';
const INVALID_ERROR = 'Enter a valid phone number for the selected country.';

/**
 * Validate a phone number against the selected country.
 *
 * Takes the ISO2 country (not the dial code) and hands the raw national
 * digits to libphonenumber-js's country-aware parser, rather than gluing
 * `dialCode + digits` into a pseudo-E.164 string ourselves. Two reasons:
 *  - A dial code isn't a country (+1 covers the US, Canada, and the rest of
 *    NANP) — passing ISO2 validates against the exact country the user
 *    picked, not whichever NANP member the digits happen to match.
 *  - libphonenumber-js's per-country metadata knows each country's national
 *    dialing conventions (e.g. a leading trunk "0" in FR/GB/DE/IT), which a
 *    naive string concat bypasses entirely.
 * @param phoneDigits - digits only, no leading dial code
 * @param countryIso2 - ISO 3166-1 alpha-2, e.g. "US"
 */
export function validatePhoneForCountry(
  phoneDigits: string,
  countryIso2: string,
): PhoneValidationResult {
  const digitsOnly = phoneDigits.replace(/\D/g, '');
  if (digitsOnly.length === 0) {
    return { valid: false, error: EMPTY_ERROR };
  }

  try {
    return isValidPhoneNumber(digitsOnly, countryIso2.toUpperCase() as CountryCode)
      ? { valid: true }
      : { valid: false, error: INVALID_ERROR };
  } catch {
    return { valid: false, error: INVALID_ERROR };
  }
}
